import { DndProvider } from "react-dnd";
import { useTranslation } from "react-i18next";

import { useState } from "react";

import { CircularProgress } from "@mui/material";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { HTML5Backend } from "react-dnd-html5-backend";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import DeleteModal from "@/components/Modals/DeleteModal";

import { ButtonColor } from "@/components/Button/types";

import {
	useCreateCompanyBalanceMutation,
	useDeleteCompanyBalanceMutation,
} from "@/pages/Private/redux/company-balance/company-balance.api";

import {
	CompanyBalance,
	CompanyBalanceArray,
	CreateCompanyBalance,
	CreateCompanyBalanceSchema,
} from "../schema/company-balance";

import EditableCompanyBalanceItem from "./EditableCompanyBalanceItem";
import EditModeCompanyBalanceItem from "./EditableCompanyBalanceItem/EditableCompanyRevenueItem";

interface EditableCompanyBalanceListProps {
	data?: CompanyBalanceArray;
	isLoading: boolean;
	editingCompanyBalanceId: number;
	setEditingCompanyBalanceId: (value: number) => void;
}

export default function EditableCompanyBalanceList({
	data,
	isLoading,
	editingCompanyBalanceId,
	setEditingCompanyBalanceId,
}: EditableCompanyBalanceListProps) {
	const { t } = useTranslation();
	const ts = (key: string, options = {}) => t(`manage-company-balance.${key}`, options);
	const [isCreating, setCreating] = useState(0);

	const [deletingCompanyBalance, setDeletingCompanyBalance] = useState<CompanyBalance | null>(null);
	const [createCompanyBalance, { isLoading: isCompanyBalanceCreating }] =
		useCreateCompanyBalanceMutation();
	const [deleteCompanyBalance, { isLoading: isCompanyBalanceDeleting }] =
		useDeleteCompanyBalanceMutation();

	const handleCreateCompanyBalance = async (values: CreateCompanyBalance) => {
		try {
			await createCompanyBalance(values).unwrap();
		} catch (error) {
			console.error(error);
		} finally {
			setCreating(0);
		}
	};

	const handleDeleteCompanyBalance = async () => {
		if (!deletingCompanyBalance?.id) {
			return;
		}

		try {
			await deleteCompanyBalance(deletingCompanyBalance?.id ?? 0).unwrap();
			setDeletingCompanyBalance(null);
		} catch (err) {
			console.error(err);
		}
	};

	const onEditCompanyBalance = (id: number) => {
		setEditingCompanyBalanceId(id);
		setCreating(0);
	};

	const handleCancel = () => {
		setEditingCompanyBalanceId(-1);
		setCreating(0);
	};

	return (
		<DndProvider backend={HTML5Backend}>
			<div
				className={`w-full border border-primary-gray-lightest p-1 pt-4 rounded-16 space-y-4 bg-[#F7F8FB]`}
			>
				<div className="flex justify-between items-center px-3">
					<h3 className="w-full flex gap-2 text-primary-blue-dark text-[14px] font-medium">
						{ts("company-balances")}

						<span className="border border-primary-gray-lightest bg-primary-gray-lightest rounded-[4px] w-5 h-5 flex items-center justify-center text-[#777D8D] text-[12px] font-medium">
							{data?.length ?? "0"}
						</span>
					</h3>
					<Button
						className="!min-w-5 !min-h-5 !max-w-5 !max-h-5 !p-0"
						color={ButtonColor.ACTION}
						image={<Icon className="w-3 h-4 text-white" icon={faPlus} />}
						title=""
						type="button"
						onClick={() => {
							setCreating(1);
							if (editingCompanyBalanceId !== -1) {
								setEditingCompanyBalanceId(-1);
							}
						}}
					/>
				</div>
				{isLoading ? (
					<div className="w-full flex justify-center py-10">
						<CircularProgress className="!w-12 !h-12" />
					</div>
				) : (
					<div className="space-y-1 relative">
						{isCompanyBalanceDeleting && (
							<div className="w-full h-full absolute flex justify-center py-12 bg-white opacity-50">
								<CircularProgress className="!w-12 !h-12" />
							</div>
						)}
						{isCreating === 1 && (
							<EditModeCompanyBalanceItem
								data={{ value: "" }}
								isLoading={isCompanyBalanceCreating}
								setEditing={handleCancel}
								validationSchema={CreateCompanyBalanceSchema}
								onSubmit={handleCreateCompanyBalance}
							/>
						)}
						{data?.length ? (
							data?.map((item) => (
								<EditableCompanyBalanceItem
									key={item?.id}
									data={item}
									isEditing={editingCompanyBalanceId === item?.id}
									isEditingInProcess={isCreating !== 0 || editingCompanyBalanceId !== -1}
									setDeletingCompanyBalance={setDeletingCompanyBalance}
									setEditing={onEditCompanyBalance}
								/>
							))
						) : (
							<p className="text-[16px] text-[#777D8D] text-center py-4">{ts("list-empty")}</p>
						)}

						{isCreating === -1 && (
							<EditModeCompanyBalanceItem
								data={{ value: "" }}
								isLoading={isCompanyBalanceCreating}
								setEditing={handleCancel}
								validationSchema={CreateCompanyBalanceSchema}
								onSubmit={handleCreateCompanyBalance}
							/>
						)}

						<Button
							className="!text-[14px] !m-3 !mt-4 !w-fit !font-medium leading-6"
							image={<Icon className="text-white w-5 h-5 mr-2" icon={faPlus} />}
							title={ts("add-company-balance")}
							type="button"
							onClick={() => {
								setCreating(-1);
								if (editingCompanyBalanceId !== -1) {
									setEditingCompanyBalanceId(-1);
								}
							}}
						/>
					</div>
				)}
			</div>
			<DeleteModal
				description={ts("delete-description", { balanceValue: deletingCompanyBalance?.value })}
				handleClose={() => setDeletingCompanyBalance(null)}
				handleDelete={handleDeleteCompanyBalance}
				isLoading={isCompanyBalanceDeleting}
				isShowing={!!deletingCompanyBalance}
				title={ts("delete-title")}
			/>
		</DndProvider>
	);
}
